
import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Lock, ArrowRight, ChevronLeft, Sparkles, AlertCircle, LogIn, UserPlus, CheckCircle2, MailCheck, RefreshCw, Send, KeyRound, Loader2, Globe } from 'lucide-react';
import { auth, googleProvider } from '../services/firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  updateProfile, 
  signInWithPopup, 
  sendEmailVerification,
  sendPasswordResetEmail,
  reload
} from '@firebase/auth';
import { saveUserProfile } from '../services/databaseService';

interface AuthProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onBack, onSuccess }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'verification' | 'reset'>('signup');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const maskPhone = (value: string) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    const limited = digits.substring(0, 11);
    if (limited.length <= 2) return limited.length > 0 ? `(${limited}` : "";
    if (limited.length <= 3) return `(${limited.substring(0, 2)}) ${limited.substring(2)}`;
    if (limited.length <= 7) return `(${limited.substring(0, 2)}) ${limited.substring(2, 3)} ${limited.substring(3)}`;
    if (limited.length <= 11) return `(${limited.substring(0, 2)}) ${limited.substring(2, 3)} ${limited.substring(3, 7)}-${limited.substring(7)}`;
    return value;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskPhone(e.target.value);
    setFormData({ ...formData, phone: masked });
  };

  const handleSocialLogin = async (provider: any) => {
    setLoading(true);
    setError("");
    try {
      // Usar Popup em vez de Redirect para maior compatibilidade e feedback imediato
      const result = await signInWithPopup(auth, provider);
      
      if (result && result.user) {
         // Salva perfil básico
         await saveUserProfile(result.user.uid, {
            displayName: result.user.displayName || 'Usuário',
            email: result.user.email || '',
            phone: ''
         });

         if (result.user.emailVerified) {
            onSuccess();
         } else {
            // Geralmente login social já vem verificado, mas por garantia
            await sendEmailVerification(result.user);
            setMode('verification');
         }
      }
    } catch (err: any) {
      console.error("Login social error:", err);
      if (err.code === 'auth/popup-closed-by-user') {
         setError("Login cancelado.");
      } else if (err.code === 'auth/popup-blocked') {
         setError("Popup bloqueado pelo navegador.");
      } else if (err.code === 'auth/unauthorized-domain') {
         setError(`DOMÍNIO NÃO AUTORIZADO: Adicione "${window.location.hostname}" no Firebase Console > Authentication > Settings > Authorized Domains.`);
      } else if (err.code === 'auth/operation-not-allowed') {
         setError("Login com Google não está ativado no Firebase Console.");
      } else {
         setError(`Erro ao iniciar login: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfoMessage("");
    
    try {
      if (mode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setError("As senhas não coincidem.");
          setLoading(false);
          return;
        }
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        await updateProfile(userCredential.user, { displayName: formData.name });
        
        await saveUserProfile(userCredential.user.uid, {
          displayName: formData.name,
          email: formData.email,
          phone: formData.phone
        });

        await sendEmailVerification(userCredential.user);
        setMode('verification');
      } else if (mode === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        if (userCredential.user.emailVerified) {
          onSuccess();
        } else {
          setMode('verification');
        }
      } else if (mode === 'reset') {
        await sendPasswordResetEmail(auth, formData.email);
        setInfoMessage("Link de redefinição enviado para seu e-mail.");
        setTimeout(() => setMode('login'), 3000);
      }
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') setError("Este e-mail já está sendo utilizado.");
      else if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found') setError("E-mail ou senha incorretos.");
      else if (err.code === 'auth/weak-password') setError("A senha deve ter pelo menos 6 caracteres.");
      else if (err.code === 'auth/network-request-failed') setError("Erro de conexão. Verifique sua internet.");
      else setError("Erro na autenticação. Verifique os dados.");
    } finally {
      setLoading(false);
    }
  };

  const checkVerification = async () => {
    setLoading(true);
    if (auth.currentUser) {
      await reload(auth.currentUser);
      if (auth.currentUser.emailVerified) {
        onSuccess();
      } else {
        setError("E-mail ainda não verificado. Verifique sua caixa de entrada.");
      }
    }
    setLoading(false);
  };

  const resendEmail = async () => {
    setLoading(true);
    if (auth.currentUser) {
      try {
        await sendEmailVerification(auth.currentUser);
        setInfoMessage("E-mail de verificação reenviado com sucesso!");
      } catch (err) {
        setError("Aguarde um pouco antes de reenviar novamente.");
      }
    }
    setLoading(false);
  };

  const inputClasses = "w-full bg-white/80 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 backdrop-blur-sm rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-800/30 transition-all outline-none font-semibold text-emerald-900 dark:text-white text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400 shadow-sm";
  const labelClasses = "text-[10px] uppercase font-black text-slate-500 dark:text-slate-400 tracking-widest block mb-2 ml-1";

  if (mode === 'verification') {
    return (
      <div className="min-h-screen mesh-gradient flex flex-col px-8 py-12 animate-fadeIn sm:items-center sm:justify-center">
        <div className="w-full sm:max-w-md text-center">
          <div className="size-24 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-500/10 border border-emerald-50">
            <MailCheck size={48} strokeWidth={2.5} className="animate-bounce" />
          </div>
          
          <h1 className="text-4xl font-black text-emerald-900 dark:text-white tracking-tighter leading-none mb-4">
            Quase lá! <br/><span className="text-emerald-600">Confirme seu E-mail</span>
          </h1>
          
          <p className="text-slate-500 dark:text-slate-400 font-bold mb-10 leading-relaxed">
            Enviamos um link de confirmação para <span className="text-emerald-700 dark:text-emerald-400">{auth.currentUser?.email}</span>. Clique no link para liberar seu acesso.
          </p>

          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-500 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-red-100 animate-shake text-left">
                <AlertCircle size={18} className="shrink-0" /> 
                <span>{error}</span>
              </div>
            )}
            
            {infoMessage && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-emerald-100">
                <CheckCircle2 size={18} /> {infoMessage}
              </div>
            )}

            <button 
              onClick={checkVerification}
              disabled={loading}
              className="w-full bg-emerald-800 dark:bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all"
            >
              {loading ? <RefreshCw className="animate-spin" size={18} /> : "Já verifiquei meu e-mail"}
            </button>

            <button 
              onClick={resendEmail}
              disabled={loading}
              className="w-full bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 py-5 rounded-[2rem] font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-slate-800 flex items-center justify-center gap-3 active:scale-95 transition-all"
            >
              Reenviar E-mail <Send size={14} />
            </button>
            
            <button 
              onClick={() => { auth.signOut(); setMode('login'); }}
              className="text-[10px] font-black uppercase text-slate-400 tracking-widest hover:text-red-500 pt-4"
            >
              Usar outro e-mail
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-8 pt-12 pb-10 animate-fadeIn sm:items-center sm:justify-center">
      <div className="w-full sm:max-w-md">
        <button 
          onClick={onBack}
          disabled={loading}
          className="w-10 h-10 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-800 mb-8 active:scale-90 transition-transform"
        >
          <ChevronLeft size={20} className="text-slate-600 dark:text-slate-400" />
        </button>

        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-800/30 rounded-full text-[9px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-4">
            <Sparkles size={10} /> 
            {mode === 'signup' ? 'O fim das planilhas começa aqui' : mode === 'reset' ? 'Recuperação de Acesso' : 'Que bom ter você de volta'}
          </div>
          <h1 className="text-4xl font-black text-emerald-900 dark:text-white tracking-tighter leading-none mb-2">
            {mode === 'signup' ? 'Criar Conta' : mode === 'reset' ? 'Redefinir Senha' : 'Acesse sua Obra'}
          </h1>
        </div>

        {mode !== 'reset' && (
          <div className="bg-stone-100 dark:bg-slate-900 p-1.5 rounded-[2rem] flex gap-1 mb-8">
            <button 
              onClick={() => { setMode('signup'); setError(""); setInfoMessage(""); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all ${mode === 'signup' ? 'bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-400 shadow-sm' : 'text-slate-400'}`}
            >
              <UserPlus size={14} /> Criar Conta
            </button>
            <button 
              onClick={() => { setMode('login'); setError(""); setInfoMessage(""); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all ${mode === 'login' ? 'bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-400 shadow-sm' : 'text-slate-400'}`}
            >
              <LogIn size={14} /> Fazer Login
            </button>
          </div>
        )}

        {mode !== 'reset' && (
          <div className="mb-6">
            <button 
              type="button"
              disabled={loading}
              onClick={() => handleSocialLogin(googleProvider)}
              className="w-full flex items-center justify-center gap-3 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm group disabled:opacity-50"
            >
               {loading ? (
                 <Loader2 size={20} className="animate-spin text-emerald-600" />
               ) : (
                 <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                 </svg>
               )}
               <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                 {loading ? 'Redirecionando...' : 'Continuar com Google'}
               </span>
            </button>
            <p className="text-[9px] text-center text-slate-400 mt-2 font-medium">
               Se ocorrer erro, verifique se o domínio {window.location.hostname} está autorizado no Firebase.
            </p>
          </div>
        )}

        <div className="relative mb-6">
           <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-800"></div></div>
           <div className="relative flex justify-center text-xs uppercase"><span className="bg-slate-50 dark:bg-slate-950 px-2 text-slate-400 font-bold tracking-widest">Ou com e-mail</span></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-500 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-red-100 animate-shake text-left">
              <AlertCircle size={18} className="shrink-0" /> 
              <span>{error}</span>
            </div>
          )}
          
          {infoMessage && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-emerald-100">
              <CheckCircle2 size={18} /> {infoMessage}
            </div>
          )}

          {mode === 'signup' && (
            <div className="relative animate-fadeIn">
              <label className={labelClasses}>Seu Nome</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60 dark:text-slate-400" size={18} />
                <input 
                  type="text" required={mode === 'signup'} placeholder="Ex: João Silva" 
                  className={inputClasses}
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <div className="relative">
            <label className={labelClasses}>E-mail</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60 dark:text-slate-400" size={18} />
              <input 
                type="email" required placeholder="seu@email.com" 
                className={inputClasses}
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                disabled={loading}
              />
            </div>
          </div>

          {mode !== 'reset' && (
            <>
              <div className="relative">
                <label className={labelClasses}>Senha</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60 dark:text-slate-400" size={18} />
                  <input 
                    type="password" required placeholder="••••••••" 
                    className={inputClasses}
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>

              {mode === 'signup' && (
                <div className="relative animate-fadeIn">
                  <label className={labelClasses}>Confirmar Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60 dark:text-slate-400" size={18} />
                    <input 
                      type="password" required placeholder="••••••••" 
                      className={inputClasses}
                      value={formData.confirmPassword}
                      onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {mode === 'signup' && (
                <div className="relative animate-fadeIn">
                  <label className={labelClasses}>WhatsApp (Opcional)</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-800/60 dark:text-slate-400" size={18} />
                    <input 
                      type="tel" placeholder="(00) 0 0000-0000" 
                      className={inputClasses}
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {mode === 'login' && (
            <div className="flex justify-end">
              <button 
                type="button" 
                onClick={() => setMode('reset')}
                className="text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-400 tracking-widest hover:underline"
              >
                Esqueci minha senha
              </button>
            </div>
          )}

          <div className="pt-4">
            <button 
              type="submit"
              disabled={loading}
              className={`w-full bg-emerald-800 dark:bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all ${loading ? 'opacity-50' : 'animate-pulsar-premium'}`}
            >
              {loading ? 'Aguarde...' : 
               mode === 'signup' ? 'Cadastrar e Validar' : 
               mode === 'reset' ? 'Enviar Link de Recuperação' : 'Acessar App'}
              {!loading && <ArrowRight size={18} />}
            </button>
          </div>
          
          {mode === 'reset' && (
             <button 
              type="button" 
              onClick={() => setMode('login')}
              className="w-full text-center text-[10px] font-black uppercase text-slate-500 tracking-widest hover:text-emerald-600"
             >
               Voltar para o Login
             </button>
          )}
        </form>
      </div>
    </div>
  );
};
